import { useGetContact, usePatchContact } from "@api";
import type { ObjectId } from "@bbj-types/utils";
import { AutocompletePlaces } from "@inputs/autocomplete/AutocompletePlacesUniq";
import { TextFieldForm } from "@inputs/hook-form";
import { PhoneInputV2 } from "@inputs/text/PhoneInputV2";
import { Button, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { Dialog } from "@surfaces/Dialog";
import { Loading } from "@surfaces/Loading";
import { isAxiosError } from "axios";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { modalEditContactResolver } from "./resolver";

export interface IModalEditContactProps {
  open: boolean;
  onClose: () => void;
  contactId: ObjectId | null;
}

export const ModalEditContact = ({ open, onClose, contactId }: IModalEditContactProps) => {
  const [{ data, loading: loadingGet }, getContact] = useGetContact();
  const [{ loading: loadingPatch }, patchContact] = usePatchContact();

  const { control, handleSubmit, reset } = useForm({
    resolver: modalEditContactResolver,
  });

  const submit = handleSubmit(async (data) => {
    if (!contactId || !data.place) {
      return;
    }
    try {
      await patchContact(contactId, { ...data, place: data.place._id });
      onClose();
      window.publish("feedback", {
        severity: "success",
        message: "Contact modifié avec succès",
      });
    } catch (error) {
      window.publish("feedback", {
        severity: "error",
        message:
          (isAxiosError(error) && error.response?.data?.message) ||
          "Une erreur est survenue lors de la mise à jour du contact. Veuillez contacter un administrateur.",
      });
    }
  });

  useEffect(() => {
    if (contactId) {
      getContact(contactId);
    }
  }, [contactId, getContact]);

  useEffect(() => {
    if (data) {
      reset({
        firstName: data.firstName,
        lastName: data.lastName,
        job: data.job,
        email: data.email,
        phone: data.phone,
        place: data.place,
        description: data.description,
      });
    }
  }, [data, reset]);

  return (
    <Dialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
      <DialogTitle>
        <Typography variant="h4">Nouveau contact</Typography>
      </DialogTitle>
      <DialogContent>
        <Loading loading={loadingGet}>
          <form>
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant="caption">
                  Pour un contact, il faut a minima un email ou un téléphone et une zone géographique.
                </Typography>
              </Grid>
              <Grid size={6}>
                <TextFieldForm
                  control={control}
                  name="firstName"
                  defaultValue=""
                  label="Prénom"
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <TextFieldForm control={control} name="lastName" defaultValue="" label="Nom" size="small" fullWidth />
              </Grid>
              <Grid size={6}>
                <TextFieldForm control={control} name="job" defaultValue="" label="Poste" size="small" fullWidth />
              </Grid>
              <Grid size={6}>
                <TextFieldForm control={control} name="email" defaultValue="" label="Email" size="small" fullWidth />
              </Grid>
              <Grid size={6}>
                <Controller
                  control={control}
                  name="phone"
                  defaultValue=""
                  render={({ field, fieldState }) => (
                    <PhoneInputV2
                      label="Téléphone"
                      value={field.value}
                      onChange={field.onChange}
                      size="small"
                      fullWidth
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={6}>
                <Controller
                  control={control}
                  name="place"
                  render={({ field, fieldState }) => (
                    <AutocompletePlaces
                      value={field.value || null}
                      onChange={(_, value) => field.onChange(value || undefined)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Zone géographique *"
                          size="small"
                          fullWidth
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Typography variant="caption">
                  Donnez le maximum d&apos;informations sur ce contact : quelle est la nature de la relation ? comment
                  transmettre les candidatures ? etc.
                </Typography>
              </Grid>
              <Grid size={12}>
                <TextFieldForm
                  control={control}
                  name="description"
                  label="Commentaires"
                  defaultValue=""
                  multiline
                  minRows={3}
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
          </form>
        </Loading>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" disabled={loadingPatch} onClick={onClose}>
          Annuler
        </Button>
        <Button variant="contained" color="primary" disabled={loadingPatch} loading={loadingPatch} onClick={submit}>
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export * from "./provider";
